import React, { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { useAsyncEffect } from '@hooks/index'
import SideNavItem from '@layout/SideNavMenu/SideNavItem'
import { ClientService } from '@services/index'
import { useSession } from '@stores/globalData/globalData.store'
import { ApiRoutes } from '@utils/api-routes'
import http from '@utils/http'
import i18n from '@utils/i18n'
import PageRoutes from '@utils/page-routes'

import type Community from '@models/Community'
import type Domain from '@models/Domain'

const CommunityNav: React.FC = () => {
  const { t } = useTranslation()
  const session = useSession()

  const [communities, setCommunities] = useState<Community[]>([])
  const [domains, setDomains] = useState<Domain[]>([])

  useAsyncEffect(async (isActive) => {
    if (session.current_user?.sites_enabled) {
      const [resp] = await ClientService.fetchCommunities()
      if (resp && isActive()) {
        const { data } = resp
        setCommunities(data.communities)
        setDomains(data.domains)
      }
    }
  }, [])

  if (!session.current_user?.sites_enabled) return null

  const changeCommunity = (community): void => {
    if (community.hostname === session.community.hostname) window.location.href = '/home'
    else {
      http
        .post(ApiRoutes.clientChangeCommunityPath(), {
          hostname: community.hostname,
        })
        .then(([resp]) => {
          if (resp?.data) window.location.href = decodeURI(resp.data.url)
        })
    }
  }

  return (
    <>
      {communities.map((community) => (
        <SideNavItem
          onClick={() => changeCommunity(community)}
          selected={session.community.hostname === community.hostname}
          label={getCommunityLabel(community, session)}
          key={community.id}
        />
      ))}
      {domains.map((domain) => (
        <SideNavItem
          onClick={() => {
            window.location.href = domain.admin_url
          }}
          selected={session.community.hostname === domain.admin_url}
          label={t('siteSidebar.domainAdmin', { name: domain.name })}
          key={domain.name}
        />
      ))}

      {/* Create Site */}
      {session.current_user.can_create_site && (
        <SideNavItem
          href={PageRoutes.siteAdminNewSitePath()}
          external
          label={t('siteSidebar.newSite')}
          iconName="nf-create"
        />
      )}
    </>
  )
}

export default CommunityNav

function getCommunityLabel(community, session): string {
  if (community.active) return community.name
  return i18n.t(
    session.current_user.can_create_site ? 'siteSidebar.activateSite' : 'siteSidebar.inactiveSite',
    {
      name: community.name,
    }
  )
}
